import type {
  IStAdjustmentGroup,
  IStAdjustmentProposal,
  IStAdjustmentPushProgress,
  StAdjustmentPushStatus,
} from "./types";

// A rollup row that can still be pushed: it names a technician to push to, and
// some of their lines haven't landed yet. The predicate narrows the optional
// `employee_id` to a number, so callers stop casting it.
export const isPushableGroup = (
  group: IStAdjustmentGroup,
): group is IStAdjustmentGroup & { employee_id: number } =>
  !!group.employee_id && group.pending > 0;

export const pushableGroups = (
  groups: IStAdjustmentGroup[],
): (IStAdjustmentGroup & { employee_id: number })[] =>
  groups.filter(isPushableGroup);

// Serialize a proposal's line key ([invoice, employee, category]) to a stable
// string — the React key for a drill-down row.
export const serializeStAdjustmentKey = (
  key: IStAdjustmentProposal["key"],
): string => key.join("|");

export const proposalKey = (proposal: IStAdjustmentProposal): string =>
  serializeStAdjustmentKey(proposal.key);

// ---- Push progress ----

// Statuses the server reports while a batch is still working.
export const isPushInFlight = (
  status: StAdjustmentPushStatus | undefined,
): boolean => status === "queued" || status === "running";

// The batch is over, either way. Both stamp `finished_at`, so both are told apart
// from an earlier batch by identity.
export const isPushTerminal = (
  status: StAdjustmentPushStatus | undefined,
): boolean => status === "completed" || status === "failed";

// Whether the WORKER has picked the batch up, as opposed to the API merely
// reserving a slot for it.
//
// The API writes `queued` twice for different things: `mark_queued` reserves the
// in-flight slot at enqueue time with no `started_at`, and the job's own `queue`
// stamps `started_at` when it actually begins. `started_at` is therefore the only
// way to tell "Sidekiq is working on this" from "a slot is held for a job that
// may never run" — and mistaking the second for the first cancels the startup
// grace window, stranding the UI on a permanent "Pushing…".
export const hasWorkerStarted = (
  progress: IStAdjustmentPushProgress | undefined,
): boolean =>
  progress?.status === "running" ||
  (progress?.status === "queued" && !!progress.started_at);

// The push endpoints return as soon as the job is ENQUEUED, but the worker only
// records progress once it actually starts (it builds the proposal list first,
// which reads the workbook). Until then the status endpoint reports whatever it
// held before — `idle` on a first push, or the PREVIOUS batch's `completed`
// record on a repeat push. Polling has to survive both of those, or it stops
// before the header action or progress toast can show that work started.
//
// `awaitingStart` is therefore the caller's business to clear — on the worker's
// first genuine report, or on its own grace timeout. See `useStAdjustmentPush`.
export const shouldPollPushStatus = (
  status: StAdjustmentPushStatus | undefined,
  awaitingStart: boolean,
): boolean => isPushInFlight(status) || awaitingStart;

// Identity of a finished batch, for announcing each outcome exactly once.
export const batchId = (
  progress: IStAdjustmentPushProgress | undefined,
): string => progress?.finished_at ?? "latest";

// Whether a status read reflects the batch we just enqueued, rather than the
// leftover record of an earlier one. `previousFinishedAt` is the `finished_at`
// observed at push time; a terminal batch carrying that same stamp is history.
//
// A bare slot reservation deliberately does NOT count — see `hasWorkerStarted`.
export const hasWorkerReported = (
  progress: IStAdjustmentPushProgress | undefined,
  previousFinishedAt: string | null,
): boolean => {
  if (hasWorkerStarted(progress)) return true;
  if (!isPushTerminal(progress?.status)) return false;

  return (progress?.finished_at ?? null) !== previousFinishedAt;
};
